/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip as MuiTooltip } from '@mui/material';
import API_BASE_URL from 'src/constants';
import Image from 'src/components/ui/Image/Image';
import Icon from 'src/components/ui/Icon/Icon';
import Divider from 'src/components/ui/Divider/Divider';
import ReturnImageGallery, { GalleryImage } from 'src/pages/ReturnOverlay/ReturnImageGallery';
import { InboundPhoto } from 'src/services/inbound';

type Props = {
  photos: InboundPhoto[];
};

// Same thumbnail-grid + lightbox pattern as the return-attachments gallery
// (src/pages/ReturnOverlay/ReturnOverlay.tsx:261-299), reusing its ReturnImageGallery
// lightbox. Kept as its own component (rather than inlined into
// InboundConfiguratorForm) so it can render `null` independently when there are no
// photos, matching InboundBackordersCard's sibling pattern.
const InboundPhotoGallery: React.FC<Props> = ({ photos }) => {
  const { t } = useTranslation();
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);

  // Defensive filter — the backend marks these as photos, but only image
  // mimetypes are safe to render inline (mirrors the ReturnOverlay filter).
  const imagePhotos: GalleryImage[] = photos
    .filter(p => p.mimetype?.startsWith('image/'))
    .map(p => ({ id: p.id, name: p.name ?? '' }));

  if (imagePhotos.length === 0) return null;

  return (
    <div className="flex flex-col gap-y-16">
      <Divider className="" />
      {/* Matches InboundConfiguratorForm's local `SectionHeadline` (h2, typo-title-24) —
          inlined rather than imported to avoid a circular import, since this component is
          itself rendered from within InboundConfiguratorForm. */}
      <h2 className="typo-title-24">{t('inbound.photos_title')}</h2>
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-12">
        {imagePhotos.map((photo, idx) => (
          <MuiTooltip key={photo.id} title={t('returns.open_gallery')}>
            <button
              type="button"
              onClick={() => setGalleryIndex(idx)}
              className="aspect-square overflow-hidden rounded-base border-subtle bg-surface-base transition-transform duration-150 hover:scale-105 hover:shadow-md">
              <Image
                imageUrl={`${API_BASE_URL}portal/utility/attachments/${photo.id}/view`}
                alt={photo.name || `attachment-${photo.id}`}
                className="w-full h-full object-cover"
                fallbackComponent={<Icon name="imageUpload" width={24} height={24} />}
              />
            </button>
          </MuiTooltip>
        ))}
      </div>
      <ReturnImageGallery
        images={imagePhotos}
        index={galleryIndex ?? 0}
        onIndexChange={setGalleryIndex}
        isOpen={galleryIndex !== null}
        onClose={() => setGalleryIndex(null)}
      />
    </div>
  );
};

export default InboundPhotoGallery;
